import type { FC, RefObject } from "react";
import React, { Suspense, useEffect, useRef } from "react";

import { Modal, type ModalHandle } from "@9amhealth/shared";
import { keyframes } from "@emotion/css";
import styled from "@emotion/styled";
import { useMediaQuery } from "@react-hooks-library/core";
import clsx from "clsx";
import { useSearchParams } from "react-router-dom";
import { IconCrossXCloseMissed } from "src/constants/icons";
import { APP_CONTENT_WIDTH_WITHOUT_PADDING } from "src/constants/layout";
import useGoToOrBack from "src/ui/hooks/useGoToOrBack";
import LocalErrorBoundary from "../SentryBoundary/LocalErrorBoundary";
import { ErrorContent } from "../SentryBoundary/ErrorPage";
import { AppQueryPopupsController } from "../AppQueryPopups/AppQueryPopupsBloc";

const FLOATING_DIALOG_MIN_WIDTH_PX = 600;
const BACKDROP_OPACITY = "0.5";
const ALWAYS_MODAL_INSET = "2rem";
const ALWAYS_MODAL_RADIUS = "1rem";

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  flex: 0 0 auto;
  background: var(--toolbar-background, var(--dialog-background));
  min-height: 3rem;
`;

const Title = styled.div`
  font-size: 1rem;
  font-weight: 500;
  text-align: center;
  flex: 1 1 auto;
`;

const CloseButton = styled.button`
  flex: 0 0 auto;
  background: transparent;
  border: 0;
  padding: 0.25rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 2rem;
    height: auto;
    color: var(--color-charcoal);
  }
`;

const HeaderSpacer = styled.div`
  flex: 0 0 auto;
  width: 2.5rem;
`;

const Content = styled.div<{
  $padding: boolean;
  $noPadding: boolean;
  $disableScroll: boolean;
}>`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: ${({ $disableScroll }) => ($disableScroll ? "hidden" : "auto")};
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  background: var(--dialog-background);
  padding: ${({ $padding, $noPadding }) =>
    $noPadding ? "0" : $padding ? "1rem" : "0"};
  position: relative;

  & > *:last-child {
    padding-bottom: ${({ $noPadding }) =>
      $noPadding ? "0" : "calc(var(--keyboard-height, 0px) + 2rem)"};
  }

  nine-funnel-step {
    --margin-top: 1rem;
    width: min(100%, 100vw, ${APP_CONTENT_WIDTH_WITHOUT_PADDING}px);
    --funnel-step-padding: 0;
  }

  &[data-dialog-type="feedback"] nine-funnel-step {
    width: 100%;
    --funnel-step-width: min(100%, 55rem);
  }
`;

const fadeInKeyframes = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

const BgGradient = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -1;

  &::before {
    content: "";
    opacity: 0.75;
    position: absolute;
    inset: 0;
    background:
      linear-gradient(
        180deg,
        var(--color-cream) 0%,
        var(--color-cream-transparent) 90%
      ),
      linear-gradient(
        85deg,
        var(--color-sunrise-blue) 1.49%,
        var(--color-sunrise-pink) 46.22%,
        var(--color-sunrise-orange) 81.11%
      );
  }

  animation: ${fadeInKeyframes} 1.2s linear forwards;
`;

interface InDialogProps {
  padding?: "none" | "default";
  isOpen?: boolean;
  hideHeader?: boolean;
  returnUrl?: string;
  isFullScreen?: boolean;
  alwaysUseReturnUrl?: boolean;
  onClose?: () => void;
  onWillDismiss?: () => void;
  children?: React.ReactNode;
  title?: string;
  setRef?: (ref: RefObject<ModalHandle | null>) => void;
  popup?: boolean;
  className?: string;
  simple?: boolean;
  backdropDismiss?: boolean;
  height?: string;
  width?: string;
  alwaysModal?: boolean;
  showClose?: boolean;
  dialogType?: string;
  background?: string;
  noPadding?: boolean;
  /**
   * Skip Content's own overflow-y scroll. Use when the child manages its
   * own scrolling (e.g. an iframe) so we don't nest scroll contexts.
   */
  disableContentScroll?: boolean;
}

interface ModalStyleArgs {
  background?: string;
  zIndex?: string;
  height?: string;
  width?: string;
  applyCallerSize: boolean;
  alwaysModal?: boolean;
  isFullScreen?: boolean;
}

function buildModalStyle(args: ModalStyleArgs): React.CSSProperties {
  const style: Record<string, string> = {
    "--backdrop-opacity": BACKDROP_OPACITY
  };
  // "gradient" is a sentinel handled by BgGradient — don't forward it to CSS.
  if (args.background && args.background !== "gradient") {
    style["--dialog-background"] = args.background;
  }
  if (args.zIndex) {
    style["--z-index"] = args.zIndex;
  }
  if (args.applyCallerSize) {
    if (args.height) {
      style["--height"] = args.height;
    }
    if (args.width) {
      style["--width"] = args.width;
    }
  }
  if (args.alwaysModal) {
    style["--max-width"] = `calc(100vw - ${ALWAYS_MODAL_INSET})`;
    style["--max-height"] = `calc(100vh - ${ALWAYS_MODAL_INSET})`;
    style["--border-radius"] = ALWAYS_MODAL_RADIUS;
  }
  if (args.isFullScreen) {
    style["--height"] = "100vh";
    style["--width"] = "100vw";
    style["--max-width"] = "100vw";
    style["--max-height"] = "100vh";
  }
  return style as React.CSSProperties;
}

const InDialog: FC<InDialogProps> = (props) => {
  const modal = useRef<ModalHandle>(null);
  const { isOpen = true, showClose = true } = props;
  const [query] = useSearchParams();
  const isFloatingViewport = useMediaQuery(
    `(min-width: ${FLOATING_DIALOG_MIN_WIDTH_PX}px)`
  );

  const zIndexParam = query.get("z");
  const zIndex = zIndexParam && zIndexParam !== "0" ? zIndexParam : undefined;

  const navigate = useGoToOrBack();
  const handleClose = () => {
    if (props.returnUrl) {
      navigate(props.returnUrl, {
        multiBack: true
      });
    }

    props.onWillDismiss?.();
    props.onClose?.();
    AppQueryPopupsController.closePopup({ dismissModal: false });
  };

  const handleDidDismiss = () => {
    handleClose();
  };

  useEffect(() => {
    const currentModal = modal.current;

    AppQueryPopupsController.setModalDismiss(currentModal);
    props.setRef?.(modal);

    return () => {
      if (AppQueryPopupsController.dismissModal === currentModal) {
        AppQueryPopupsController.setModalDismiss(null);
      }
    };
  }, []);

  const style = buildModalStyle({
    background: props.background,
    zIndex,
    height: props.height,
    width: props.width,
    applyCallerSize: isFloatingViewport || Boolean(props.alwaysModal),
    alwaysModal: props.alwaysModal,
    isFullScreen: props.isFullScreen
  });

  if (!props.children) {
    return null;
  }

  return (
    <Modal
      ref={modal}
      isOpen={isOpen}
      variant="center"
      backdropDismiss={props.backdropDismiss}
      onWillDismiss={props.onWillDismiss}
      onDidDismiss={handleDidDismiss}
      className={clsx(
        props.className,
        props.simple && "modal-simple",
        props.alwaysModal && "modal-always-modal"
      )}
      style={style}
      dataAttributes={{
        "data-custom-z-index": Boolean(zIndex),
        "data-fullscreen": props.isFullScreen,
        "data-dialog-type": props.dialogType
      }}
    >
      {!props.hideHeader && (
        <Header className="header">
          <HeaderSpacer className="header-spacer" />
          {props.title ? (
            <Title className="title">{props.title}</Title>
          ) : (
            <Title className="title" />
          )}
          {showClose ? (
            <CloseButton
              type="button"
              aria-label="Close"
              onClick={() => modal.current?.dismiss()}
              className="close"
            >
              <IconCrossXCloseMissed aria-hidden="true" />
            </CloseButton>
          ) : (
            <HeaderSpacer />
          )}
        </Header>
      )}
      <Content
        $padding={props.padding !== "none" && !props.simple}
        $noPadding={Boolean(props.noPadding) || Boolean(props.simple)}
        $disableScroll={Boolean(props.disableContentScroll)}
        data-dialog-type={props.dialogType}
      >
        <LocalErrorBoundary fallback={<ErrorContent />}>
          <Suspense fallback={<div />}>{props.children}</Suspense>
        </LocalErrorBoundary>
        {props.background === "gradient" && <BgGradient />}
      </Content>
    </Modal>
  );
};

export default InDialog;
